// Scorched burned tiles, from design/hot-type/boards/BurnCatch.html (ending A). At rest a burned tile
// is red paper with a ragged charred rim and a glowing burn line, shaped by a seed so no two match.
// The tile that just burned flips in with its row still armed; then the flame lights the fuse at its
// corner, the spark runs one lap of the edge burning the outline away, and the fire spreads across the
// tile from the flame. Tile draws this under the letter; the motion is in app.css.
import { memo, useId, type CSSProperties } from 'react'

/** Smaller tiles (the side panels, the phoenix intro) keep the flat red look. */
export const SCORCH_MIN = 30

// After Enter, in ms: the tile flips in (0.4 s, 70 ms later per column), the fuse is lit a beat after it
// lands, the lap takes LAP (--lap in app.css), and the fire takes SPREAD to cross the tile.
const FLIP = 400, LIGHT = 60, LAP = 560, SPREAD = 720
const timing = (col: number) => { const flip = col * 70, ls = flip + FLIP + LIGHT; return { flip, ls, igd: ls + LAP } }
/** When a burn tile in this column catches, and when it has settled (line cooled, smoke gone). */
export const catchAt = (col: number) => timing(col).igd
export const catchDone = (col: number) => timing(col).igd + 2500

/** Whether a tile this size plays the catch: not small ones, and not with reduced motion. */
export const canCatch = (size: number) => size >= SCORCH_MIN && !matchMedia('(prefers-reduced-motion: reduce)').matches

type Pt = [number, number]
interface XY { x: number; y: number }

function rng(seed: number) {
  let a = (seed * 2654435761) >>> 0
  return () => {
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}
const D = (pts: Pt[]) => `M${pts.map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join('L')}Z`

// The tile's 0–100 box. rr is a rounded square; border() is the middle of a 2px outline at this size.
const rr = (a: number, b: number, r: number) => `M${a + r} ${a}H${b - r}A${r} ${r} 0 0 1 ${b} ${a + r}V${b - r}A${r} ${r} 0 0 1 ${b - r} ${b}H${a + r}A${r} ${r} 0 0 1 ${a} ${b - r}V${a + r}A${r} ${r} 0 0 1 ${a + r} ${a}Z`
const border = (size: number) => { const a = 100 / size; return { a, b: 100 - a, r: (0.17 * size - 1) * (100 / size), w: 200 / size } }

/** The point a fraction f of the way round the outline, clockwise from the left end of the top edge. */
function outlineAt(size: number, f: number): XY {
  const { a, b, r } = border(size)
  const side = b - a - 2 * r, arc = (Math.PI * r) / 2
  let d = (((f % 1) + 1) % 1) * 4 * (side + arc)
  const starts = [[a + r, a, 1, 0], [b, a + r, 0, 1], [b - r, b, -1, 0], [a, b - r, 0, -1]]
  const centres = [[b - r, a + r], [b - r, b - r], [a + r, b - r], [a + r, a + r]]
  for (let k = 0; k < 4; k++) {
    const [x, y, dx, dy] = starts[k]
    if (d <= side) return { x: x + dx * d, y: y + dy * d }
    d -= side
    const th = -Math.PI / 2 + (k * Math.PI) / 2 + Math.min(d, arc) / r
    if (d <= arc) return { x: centres[k][0] + r * Math.cos(th), y: centres[k][1] + r * Math.sin(th) }
    d -= arc
  }
  return { x: a + r, y: a }
}
/** The fuse is lit at the middle of the top-right corner, beside the flame. */
function flameAt(size: number) {
  const { a, b, r } = border(size)
  const side = b - a - 2 * r, arc = (Math.PI * r) / 2
  return (side + arc / 2) / (4 * (side + arc))
}

// The burned shape: a ragged outer edge and a rounder burn line inside it, sampled at equal angles.
function geom(seed: number) {
  const R = rng(seed * 7 + 3)
  const harm = (ks: Pt[]) => ks.map(([k, a]) => [k, a, R() * Math.PI * 2] as const)
  const outerH = harm([[3, 1.1], [5, 0.8], [8, 0.5], [13, 0.35], [21, 0.3], [34, 0.2]])
  const innerH = harm([[2, 1.2], [3, 1], [5, 0.8], [7, 0.5], [11, 0.4], [17, 0.3], [27, 0.2]])
  const noise = (h: (readonly [number, number, number])[], th: number) => { let s = 0, m = 0; for (const [k, a, p] of h) { s += a * Math.sin(k * th + p); m += a } return (s / m + 1) / 2 }
  const sup = (th: number, e: number) => 1 / Math.pow(Math.pow(Math.abs(Math.cos(th)), e) + Math.pow(Math.abs(Math.sin(th)), e), 1 / e)
  const o: Pt[] = [], i: Pt[] = []
  for (let n = 0; n < 128; n++) {
    const th = (n / 128) * Math.PI * 2
    const ro = 50 * sup(th, 5) - (0.4 + 3.8 * Math.pow(noise(outerH, th), 2.2))
    const ri = 50 * sup(th, 3.4) * 0.9 - 6.5 * Math.pow(noise(innerH, th), 1.4)
    o.push([50 + ro * Math.cos(th), 50 + ro * Math.sin(th)])
    i.push([50 + ri * Math.cos(th), 50 + ri * Math.sin(th)])
  }
  return { o, O: D(o), I: D(i) }
}

export interface ScorchPlan {
  seed: number
  size: number
  g: ReturnType<typeof geom>
  /** The catch, when this tile is the one burning now. */
  fire?: Fire
}
interface Fire {
  P: XY
  /** The outline redrawn to start at the flame, so the lap is 0 → 100 along it. */
  lap: string
  /** A wobbly disc round P that grows by S to cover the tile: the fire's front. */
  disc: string
  S: number
  vars: CSSProperties
  sparks: CSSProperties[]
  ash: CSSProperties[]
  smoke: CSSProperties[]
}

/** The shape for a burned tile, and its catch if col is set (the column it sits in, for its timing). */
export function scorchPlan(seed: number, size: number, col?: number): ScorchPlan {
  const g = geom(seed)
  if (col === undefined) return { seed, size, g }
  const T = timing(col)
  const f0 = flameAt(size)
  const P = outlineAt(size, f0)
  const lap: Pt[] = []
  for (let k = 0; k < 120; k++) { const p = outlineAt(size, f0 + k / 120); lap.push([p.x, p.y]) }

  const R = rng(seed * 13 + 5)
  const h = [[3, 1], [5, 0.7], [8, 0.45], [13, 0.3]].map(([k, a]) => [k, a, R() * Math.PI * 2])
  const disc: Pt[] = []
  for (let n = 0; n < 48; n++) {
    const th = (n / 48) * Math.PI * 2
    let s = 0, m = 0
    for (const [k, a, p] of h) { s += a * Math.sin(k * th + p); m += a }
    const r = 6 * (1 + 0.16 * (s / m))
    disc.push([P.x + r * Math.cos(th), P.y + r * Math.sin(th)])
  }
  const far = Math.max(...[[0, 0], [100, 0], [0, 106], [100, 106]].map(([x, y]) => Math.hypot(x - P.x, y - P.y)))
  // When the fire reaches a point, in ms after Enter: the disc grows a little faster than linearly.
  const reach = (q: XY) => T.igd + SPREAD * Math.pow(Math.hypot(q.x - P.x, q.y - P.y) / far, 1.15)

  const Q = rng(seed * 17 + 11)
  const out = Math.atan2(P.y - 50, P.x - 50)
  const sparks = Array.from({ length: 4 }, () => {
    const a = out + (Q() - 0.5) * 2.2, d = 0.25 + Q() * 0.25
    return { '--x': `${P.x.toFixed(1)}%`, '--y': `${P.y.toFixed(1)}%`, '--dx': `calc(var(--t) * ${(Math.cos(a) * d).toFixed(2)})`, '--dy': `calc(var(--t) * ${(Math.sin(a) * d).toFixed(2)})` } as CSSProperties
  })
  const ash = Array.from({ length: 6 }, (_, k) => {
    const [x, y] = g.o[Math.floor(Q() * g.o.length)]
    return { '--x': `${x.toFixed(0)}%`, '--y': `${y.toFixed(0)}%`, '--ad': `${(60 + reach({ x, y })).toFixed(0)}ms`, '--dx': `calc(var(--t) * ${((Q() - 0.5) * 0.6).toFixed(2)})`, '--rot': `${((Q() - 0.5) * 540).toFixed(0)}deg`, '--c': k % 2 ? '#B9AEA0' : '#2B211C' } as CSSProperties
  })
  const smoke = Array.from({ length: 2 }, (_, k) => ({ '--x': `${(30 + Q() * 40).toFixed(0)}%`, '--sd': `${T.igd + 240 + k * 260}ms`, '--dx': `calc(var(--t) * ${((Q() - 0.5) * 0.5).toFixed(2)})` }) as CSSProperties)

  const vars = {
    '--flip': `${T.flip}ms`, '--ls': `${T.ls}ms`, '--igd': `${T.igd}ms`,
    // The flame flares as the fire reaches it; the letter turns light as the fire reaches the middle.
    '--fld': `${(reach({ x: 88, y: 12 }) - 40).toFixed(0)}ms`,
    '--cd': `${(reach({ x: 50, y: 50 }) - T.igd).toFixed(0)}ms`,
  } as CSSProperties
  return { seed, size, g, fire: { P, lap: D(lap), disc: D(disc), S: (far + 8) / (6 * 0.84), vars, sparks, ash, smoke } }
}

const MU = { maskUnits: 'userSpaceOnUse', x: -30, y: -30, width: 160, height: 160 } as const

/** The burned look under a tile's letter, plus the sparks, ash and smoke while it catches. Memoised on the plan, which the tile memoises. */
export const Scorch = memo(function Scorch({ plan }: { plan: ScorchPlan }) {
  const id = `sc${useId().replace(/[^\w-]/g, '')}`
  const { g, fire, size, seed } = plan
  const shimmer = { '--d': `${(-rng(seed * 31 + 7)() * 4.5).toFixed(2)}s` } as CSSProperties
  const defs = (
    <>
      <radialGradient id={`${id}p`} cx="50%" cy="48%" r="62%">
        <stop offset="0" style={{ stopColor: 'var(--burn)' }} />
        <stop offset="0.42" style={{ stopColor: 'var(--burn)' }} />
        <stop offset="0.74" stopColor="#9C2B15" />
      </radialGradient>
      <radialGradient id={`${id}c`} cx="50%" cy="50%" r="64%">
        <stop offset="0.62" stopColor="#6E2A12" />
        <stop offset="0.82" stopColor="#3A1408" />
        <stop offset="1" stopColor="#1A0804" />
      </radialGradient>
      <filter id={`${id}b`} x="-30%" y="-30%" width="160%" height="160%"><feGaussianBlur stdDeviation="1.8" /></filter>
    </>
  )
  const burned = (ring?: string) => (
    <>
      <path className="edge" d={g.O} transform="translate(0 5.8)" />
      <path d={g.O} fill={`url(#${id}p)`} />
      {fire && <path className="heat" d={g.O} />}
      {fire && <path className="ring-hot" d={`${g.O} ${g.I}`} fillRule="evenodd" />}
      <path d={`${g.O} ${g.I}`} fillRule="evenodd" fill={`url(#${id}c)`} mask={ring} />
      <path className="glow" d={g.I} filter={`url(#${id}b)`} />
      <path className="line" d={g.I} />
    </>
  )
  if (!fire) return <svg className="scorch" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" style={shimmer}><defs>{defs}</defs>{burned()}</svg>

  const { a, b, r, w } = border(size)
  const { P, lap, disc } = fire
  const o = { transformOrigin: `${P.x.toFixed(1)}px ${P.y.toFixed(1)}px` }
  const spark = (
    <g className="lap">
      <path className="trail" d={lap} pathLength={100} />
      <path className="halo" d={lap} pathLength={100} filter={`url(#${id}b)`} />
      <path className="core" d={lap} pathLength={100} />
    </g>
  )
  return (
    <>
      <svg className="scorch" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" style={{ ...shimmer, '--S': fire.S.toFixed(2) } as CSSProperties}>
        <defs>
          {defs}
          <radialGradient id={`${id}g`}>
            <stop offset="0" stopColor="#FFFFFF" />
            <stop offset="0.35" stopColor="#FFE9A8" />
            <stop offset="0.7" stopColor="#FF8C3C" stopOpacity="0.6" />
            <stop offset="1" stopColor="#FF6A2B" stopOpacity="0" />
          </radialGradient>
          <filter id={`${id}f`} filterUnits="userSpaceOnUse" x="-30" y="-30" width="160" height="160"><feGaussianBlur stdDeviation="2.6" /></filter>
          {/* Burned inside the disc, armed outside it, and the char a beat behind the fire. */}
          <mask id={`${id}a`} {...MU}><path className="fb" d={disc} fill="#fff" style={o} /></mask>
          <mask id={`${id}n`} {...MU}><rect x="-30" y="-30" width="160" height="160" fill="#fff" /><path className="fb" d={disc} fill="#000" style={o} /></mask>
          <mask id={`${id}l`} {...MU}><path className="fb lag" d={disc} fill="#fff" style={o} /></mask>
          {/* The outline, gone where the spark has been. */}
          <mask id={`${id}o`} {...MU}><rect x="-30" y="-30" width="160" height="160" fill="#fff" /><path className="cord" d={lap} pathLength={100} stroke="#000" strokeWidth={(w * 2.5).toFixed(2)} /></mask>
          <clipPath id={`${id}k`}><path d={rr(0, 100, 17)} /></clipPath>
        </defs>
        <g mask={`url(#${id}n)`}>
          <path className="a-edge" d={rr(0, 100, 17)} transform={`translate(0 ${(300 / size).toFixed(2)})`} />
          <path className="a-face" d={rr(a, b, r)} />
          <path className="a-line" d={rr(a, b, r)} strokeWidth={w.toFixed(2)} mask={`url(#${id}o)`} />
          <path className="cord burnt" d={lap} pathLength={100} strokeWidth={(w * 0.8).toFixed(2)} />
        </g>
        <g mask={`url(#${id}a)`}>{burned(`url(#${id}l)`)}</g>
        {spark}
        <circle className="light" cx={P.x.toFixed(1)} cy={P.y.toFixed(1)} r="7" fill={`url(#${id}g)`} />
        <g clipPath={`url(#${id}k)`}>
          <g filter={`url(#${id}f)`}><path className="front" d={disc} style={o} /></g>
          <path className="front-core" d={disc} style={o} />
        </g>
        <circle className="bite" cx={P.x.toFixed(1)} cy={P.y.toFixed(1)} r="10" fill={`url(#${id}g)`} />
      </svg>
      {fire.sparks.map((s, k) => <i key={`s${k}`} className="spk" style={s} />)}
      {fire.ash.map((s, k) => <i key={`a${k}`} className="flk" style={s} />)}
      {fire.smoke.map((s, k) => <i key={`m${k}`} className="smk" style={s} />)}
    </>
  )
})
